import React, { useEffect, useRef, useState } from "react";
import {
  View,
  StyleSheet,
  Button,
  ActivityIndicator,
  TextInput,
} from "react-native";
import { NormalText } from "../../../common/native";
import {
  autoGenerateBeforeAfter,
  getBeforeAfter,
} from "../../../../services/api-clinic";
import { TSession } from "../../../../utils/types";

type Props = {
  session: TSession;
  onLog?: (message: string) => void;
};

const AutoGenerateBeforeAfterPanel: React.FC<Props> = ({ session, onLog }) => {
  const [autoGenerateLoading, setAutoGenerateLoading] =
    useState<boolean>(false);
  const [autoGeneratePatientUuid, setAutoGeneratePatientUuid] =
    useState<string>("");
  const [autoGenerateErrors, setAutoGenerateErrors] = useState<string[] | null>(
    null,
  );
  const [showAutoGenerateErrors, setShowAutoGenerateErrors] =
    useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showToast, setShowToast] = useState<boolean>(false);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const addLog = (message: string) => {
    onLog?.(message);
  };

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => {
      setShowToast(false);
      setToastMessage(null);
    }, tokens.motion.toastMs);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const handleAutoGenerateBeforeAfter = async () => {
    if (autoGenerateLoading || !session || !session.clinicId) return;

    try {
      setAutoGenerateLoading(true);
      setAutoGenerateErrors(null);
      setShowAutoGenerateErrors(false);
      addLog("Auto-generating before/after pairs...");
      const response = await autoGenerateBeforeAfter(session, {
        patient_uuid: autoGeneratePatientUuid.trim() || null,
        limit: 100,
      });
      showToastMessage(
        `Generated ${response.pairs_generated} pairs across ${response.patients_processed} patients.`,
      );
      addLog(
        `✅ Auto-generate complete: ${response.pairs_generated} pairs across ${response.patients_processed} patients.`,
      );

      if (response.errors && response.errors.length > 0) {
        setAutoGenerateErrors(response.errors);
        addLog(`⚠️ Some patients failed. Errors: ${response.errors.length}`);
      }

      const galleryResponse = await getBeforeAfter(session, {
        limit: 20,
        sort_order: "desc",
      });
      addLog(
        `🔄 Gallery refreshed: ${galleryResponse.batch_images.length} batch image(s) loaded.`,
      );
    } catch (error) {
      addLog(`❌ Auto-generate failed: ${error}`);
    } finally {
      setAutoGenerateLoading(false);
    }
  };

  return (
    <View style={styles.section}>
      <NormalText
        text="🧩 Before/After Auto-Generate"
        style={styles.sectionTitle}
      />
      <NormalText
        text="Generate before/after pairs for postop images. Optionally limit to one patient."
        style={styles.helperText}
      />
      <View style={styles.inputGroup}>
        <NormalText text="Patient UUID (optional)" style={styles.labelText} />
        <TextInput
          placeholder="Enter patient UUID"
          value={autoGeneratePatientUuid}
          onChangeText={setAutoGeneratePatientUuid}
          style={styles.input}
          autoCapitalize="none"
        />
      </View>
      <View style={styles.buttonRow}>
        <Button
          title={
            autoGenerateLoading ? "Generating..." : "Auto-Generate Before/After"
          }
          onPress={handleAutoGenerateBeforeAfter}
          color={tokens.color.primary}
          disabled={autoGenerateLoading}
        />
      </View>
      {autoGenerateLoading && (
        <View style={styles.loadingIndicator}>
          <ActivityIndicator size="small" color={tokens.color.primary} />
          <NormalText
            text="Working in background..."
            style={styles.loadingText}
          />
        </View>
      )}
      {autoGenerateErrors && autoGenerateErrors.length > 0 && (
        <View style={styles.warningContainer}>
          <NormalText
            text="Some patients failed. See details."
            style={styles.warningText}
          />
          <Button
            title={showAutoGenerateErrors ? "Hide errors" : "Show errors"}
            onPress={() => setShowAutoGenerateErrors((current) => !current)}
            color={tokens.color.warning}
          />
          {showAutoGenerateErrors && (
            <View style={styles.errorPanel}>
              {autoGenerateErrors.map((error, index) => (
                <NormalText
                  key={`${error}-${index}`}
                  text={error}
                  style={styles.errorText}
                />
              ))}
            </View>
          )}
        </View>
      )}
      {showToast && toastMessage && (
        <View style={styles.toast}>
          <NormalText text={toastMessage} style={styles.toastText} />
        </View>
      )}
    </View>
  );
};

const tokens = {
  color: {
    primary: "#00B7F4",
    warning: "#E67E22",
    muted: "#666",
    text: "#333",
    textStrong: "#444",
    background: "#fff",
    surface: "#f8f9fa",
    border: "#e9ecef",
    borderStrong: "#ddd",
    warningText: "#8A6D3B",
    errorText: "#7A2A00",
    warningBorder: "#F0AD4E",
    toastBackground: "#333",
    toastText: "#fff",
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
  },
  radius: {
    sm: 8,
    md: 12,
  },
  font: {
    mono: "monospace",
  },
  fontWeight: {
    semibold: "600",
  },
  fontSize: {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18,
  },
  borderWidth: {
    sm: 1,
  },
  zIndex: {
    toast: 10,
  },
  motion: {
    toastMs: 2500,
  },
};

const styles = StyleSheet.create({
  section: {
    marginBottom: tokens.spacing.xl,
    padding: tokens.spacing.lg,
    backgroundColor: tokens.color.surface,
    borderRadius: tokens.radius.md,
    borderWidth: tokens.borderWidth.sm,
    borderColor: tokens.color.border,
  },
  sectionTitle: {
    fontSize: tokens.fontSize.lg,
    fontWeight: tokens.fontWeight.semibold as "600",
    color: tokens.color.text,
    marginBottom: tokens.spacing.lg,
  },
  helperText: {
    fontSize: tokens.fontSize.sm,
    color: tokens.color.muted,
    marginBottom: tokens.spacing.md,
  },
  inputGroup: {
    gap: tokens.spacing.sm,
    marginBottom: tokens.spacing.lg,
  },
  labelText: {
    fontSize: tokens.fontSize.sm,
    color: tokens.color.textStrong,
  },
  input: {
    backgroundColor: tokens.color.background,
    borderRadius: tokens.radius.sm,
    padding: tokens.spacing.md,
    borderWidth: tokens.borderWidth.sm,
    borderColor: tokens.color.borderStrong,
    fontSize: tokens.fontSize.sm,
    color: tokens.color.text,
    fontFamily: tokens.font.mono,
  },
  buttonRow: {
    flexDirection: "row",
    gap: tokens.spacing.lg,
    justifyContent: "center",
    flexWrap: "wrap",
  },
  loadingIndicator: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacing.sm,
    marginTop: tokens.spacing.lg,
  },
  loadingText: {
    color: tokens.color.muted,
    fontSize: tokens.fontSize.sm,
  },
  warningContainer: {
    marginTop: tokens.spacing.lg,
    gap: tokens.spacing.md,
  },
  warningText: {
    color: tokens.color.warningText,
    fontSize: tokens.fontSize.sm,
  },
  errorPanel: {
    backgroundColor: tokens.color.background,
    borderRadius: tokens.radius.sm,
    padding: tokens.spacing.md,
    borderWidth: tokens.borderWidth.sm,
    borderColor: tokens.color.warningBorder,
    gap: tokens.spacing.sm,
  },
  errorText: {
    color: tokens.color.errorText,
    fontSize: tokens.fontSize.xs,
  },
  toast: {
    position: "absolute",
    bottom: tokens.spacing.xl,
    left: tokens.spacing.xl,
    right: tokens.spacing.xl,
    backgroundColor: tokens.color.toastBackground,
    paddingVertical: tokens.spacing.md,
    paddingHorizontal: tokens.spacing.lg,
    borderRadius: tokens.radius.md,
    alignItems: "center",
    zIndex: tokens.zIndex.toast,
  },
  toastText: {
    color: tokens.color.toastText,
    fontSize: tokens.fontSize.sm,
    textAlign: "center",
  },
});

export default AutoGenerateBeforeAfterPanel;
